import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Chip,
  Card,
  CardContent,
  Alert,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  LocalHospital,
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  LocationOn,
  Business,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { 
  getHospitalMaster, 
  createHospitalMaster, 
  updateHospitalMaster, 
  deleteHospitalMaster,
  HospitalMaster, 
  HospitalMasterCreate 
} from '../api/api';

const HospitalManagement: React.FC = () => {
  const [hospitals, setHospitals] = useState<HospitalMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHospital, setEditingHospital] = useState<HospitalMaster | null>(null);
  const [formData, setFormData] = useState<HospitalMasterCreate>({
    name: '',
    short_name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    cnpj: '',
    contact_person: '',
    contact_phone: '',
    notes: '',
    is_active: true
  });

  const fetchHospitals = async () => {
    setLoading(true);
    try {
      const data = await getHospitalMaster({ limit: 500, active_only: false });
      setHospitals(data);
    } catch (error) {
      console.error('Error fetching hospitals:', error);
      setError('Failed to load hospitals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHospitals();
  }, []);

  const handleOpenDialog = (hospital?: HospitalMaster) => {
    if (hospital) {
      setEditingHospital(hospital);
      setFormData({
        name: hospital.name,
        short_name: hospital.short_name || '',
        address: hospital.address || '',
        city: hospital.city || '',
        state: hospital.state || '',
        postal_code: hospital.postal_code || '',
        phone: hospital.phone || '',
        email: hospital.email || '',
        website: hospital.website || '',
        cnpj: hospital.cnpj || '',
        contact_person: hospital.contact_person || '',
        contact_phone: hospital.contact_phone || '',
        notes: hospital.notes || '',
        is_active: hospital.is_active
      });
    } else {
      setEditingHospital(null);
      setFormData({
        name: '',
        short_name: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        phone: '',
        email: '',
        website: '',
        cnpj: '',
        contact_person: '',
        contact_phone: '',
        notes: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingHospital(null);
  };

  const handleInputChange = (field: keyof HospitalMasterCreate) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Hospital name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingHospital) {
        await updateHospitalMaster(editingHospital.id, formData);
        setSuccess('Hospital updated successfully');
      } else {
        await createHospitalMaster(formData);
        setSuccess('Hospital created successfully');
      }
      handleCloseDialog();
      fetchHospitals();
    } catch (error) {
      setError('Failed to save hospital');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (hospital: HospitalMaster) => {
    if (window.confirm(`Are you sure you want to deactivate ${hospital.name}?`)) {
      try {
        await deleteHospitalMaster(hospital.id);
        setSuccess('Hospital deactivated successfully');
        fetchHospitals();
      } catch (error) {
        setError('Failed to deactivate hospital');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={40} sx={{ color: '#6366f1' }} />
        <Typography variant="body2" color="text.secondary">
          Loading hospitals...
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Hospital Management
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              Manage hospital master data and information
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Add Hospital
          </Button>
        </Box>

        {/* Statistics Card */}
        <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <LocalHospital sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {hospitals.length}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Hospitals
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {hospitals.filter(h => h.is_active).length} active
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Hospitals Table */}
        <Paper 
          sx={{ 
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#6366f1', 0.05) }}>
                  <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalHospital sx={{ fontSize: 16, color: '#6366f1' }} />
                      Name
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn sx={{ fontSize: 16, color: '#6366f1' }} />
                      Location
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone sx={{ fontSize: 16, color: '#6366f1' }} />
                      Contact
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hospitals.map((hospital, index) => (
                  <TableRow 
                    key={hospital.id} 
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha('#6366f1', 0.02),
                        transition: 'all 0.2s ease'
                      },
                      borderBottom: index === hospitals.length - 1 ? 'none' : '1px solid #e2e8f0'
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {hospital.name}
                        </Typography>
                        {hospital.short_name && (
                          <Typography variant="caption" color="text.secondary">
                            {hospital.short_name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {hospital.address && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {hospital.address}
                          </Typography>
                        )}
                        {(hospital.city || hospital.state) && (
                          <Typography variant="caption" color="text.secondary">
                            {[hospital.city, hospital.state].filter(Boolean).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {hospital.phone && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <Phone sx={{ fontSize: 14, mr: 1, verticalAlign: 'middle' }} />
                            {hospital.phone}
                          </Typography>
                        )}
                        {hospital.email && (
                          <Typography variant="body2">
                            <Email sx={{ fontSize: 14, mr: 1, verticalAlign: 'middle' }} />
                            {hospital.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={hospital.is_active ? <CheckCircle /> : <Cancel />}
                        label={hospital.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          backgroundColor: hospital.is_active 
                            ? alpha('#10b981', 0.1) 
                            : alpha('#ef4444', 0.1),
                          color: hospital.is_active ? '#10b981' : '#ef4444',
                          fontWeight: 600,
                          borderRadius: 2
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(hospital)}
                            sx={{ color: '#6366f1' }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {hospital.is_active && (
                          <Tooltip title="Deactivate">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(hospital)}
                              sx={{ color: '#ef4444' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {hospitals.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <LocalHospital sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
              No hospitals found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Add your first hospital to start managing hospital master data.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Add First Hospital
            </Button>
          </Box>
        )}

        {/* Hospital Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingHospital ? 'Edit Hospital' : 'Add New Hospital'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Hospital Name *"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Short Name"
                  value={formData.short_name}
                  onChange={handleInputChange('short_name')}
                  variant="outlined"
                />
              </Stack>
              
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={handleInputChange('address')}
                variant="outlined"
                multiline
                rows={2}
              />
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={handleInputChange('city')}
                  variant="outlined"
                />
                <TextField
                  label="State"
                  value={formData.state}
                  onChange={handleInputChange('state')}
                  variant="outlined"
                  sx={{ minWidth: 120 }}
                />
                <TextField
                  label="Postal Code"
                  value={formData.postal_code}
                  onChange={handleInputChange('postal_code')}
                  variant="outlined"
                  sx={{ minWidth: 120 }}
                />
              </Stack>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  variant="outlined"
                />
              </Stack>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={handleInputChange('website')}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="CNPJ"
                  value={formData.cnpj}
                  onChange={handleInputChange('cnpj')}
                  variant="outlined"
                />
              </Stack>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contact_person}
                  onChange={handleInputChange('contact_person')}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange('contact_phone')}
                  variant="outlined"
                />
              </Stack>
              
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleInputChange('notes')}
                variant="outlined"
                multiline
                rows={3}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              {saving ? 'Saving...' : (editingHospital ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Messages */}
        {success && (
          <Alert 
            severity="success" 
            onClose={() => setSuccess(null)}
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          >
            {error}
          </Alert>
        )}
      </Box>
    </Fade>
  );
};

export default HospitalManagement;